import type { EmailEditorState, EmailEditorContext } from './types';

const CURRENCY_LABELS: Record<string, string> = {
  SAR: 'ر.س', USD: '$', EUR: '€', AED: 'د.إ', KWD: 'د.ك',
  BHD: 'د.ب', QAR: 'ر.ق', OMR: 'ر.ع', EGP: 'ج.م',
};

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function interpolateVars(text: string, ctx?: EmailEditorContext): string {
  if (!text || !ctx) return text;
  return text
    .replace(/\{name\}/g, ctx.customerName || '')
    .replace(/\{storeName\}/g, ctx.storeName || '');
}

function buildItemsHtml(items: EmailEditorContext['items'], currency?: string): string {
  if (!items || items.length === 0) return '';
  const cl = CURRENCY_LABELS[currency || 'SAR'] || currency || 'SAR';
  let total = 0;

  const rows = items.map(item => {
    const itemTotal = (item.price || 0) * (item.quantity || 1);
    total += itemTotal;
    const imgCell = item.image
      ? `<td style="padding:10px 12px;border-bottom:1px solid #f0f0f3"><img src="${escapeHtml(item.image)}" alt="" style="width:44px;height:44px;object-fit:cover;border-radius:6px"></td>`
      : '';
    return `<tr>${imgCell}<td style="padding:10px 12px;border-bottom:1px solid #f0f0f3;font-size:14px">${escapeHtml(item.title)}</td><td style="padding:10px 12px;border-bottom:1px solid #f0f0f3;font-size:14px">${item.quantity}</td><td style="padding:10px 12px;border-bottom:1px solid #f0f0f3;font-size:14px">${item.price.toFixed(2)} ${cl}</td></tr>`;
  }).join('');

  const hasImg = items.some(i => i.image);
  return `<table style="width:100%;border-collapse:collapse;margin:16px 0">
    <thead><tr>${hasImg ? '<th style="background:#f8f8fa;padding:8px 12px;text-align:right;font-size:12px;color:#888;border-bottom:1px solid #e8e8ed"></th>' : ''}<th style="background:#f8f8fa;padding:8px 12px;text-align:right;font-size:12px;color:#888;border-bottom:1px solid #e8e8ed">المنتج</th><th style="background:#f8f8fa;padding:8px 12px;text-align:right;font-size:12px;color:#888;border-bottom:1px solid #e8e8ed">الكمية</th><th style="background:#f8f8fa;padding:8px 12px;text-align:right;font-size:12px;color:#888;border-bottom:1px solid #e8e8ed">السعر</th></tr></thead>
    <tbody>${rows}<tr>${hasImg ? '<td></td>' : ''}<td colspan="2" style="padding:12px;font-weight:700;border-top:2px solid #e8e8ed">الإجمالي</td><td style="padding:12px;font-weight:700;border-top:2px solid #e8e8ed">${total.toFixed(2)} ${cl}</td></tr></tbody>
  </table>`;
}

export function buildEmailHtml(state: EmailEditorState, context?: EmailEditorContext): string {
  const { colors } = state;
  const storeName = context?.storeName || '';
  const greeting = interpolateVars(state.greeting, context);
  const body = interpolateVars(state.bodyHtml || '', context);
  const ctaText = state.ctaText;
  const ctaUrl = state.ctaUrl || context?.storeUrl || '#';

  const showItems = !!(context?.items && context.items.length > 0);
  const showCta = !!ctaText;
  const showFooter = true;

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(state.subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:${colors.backgroundColor};font-family:'Segoe UI',Tahoma,Arial,sans-serif">
  <div style="width:100%;background-color:${colors.backgroundColor};padding:32px 0">
    <div style="max-width:600px;margin:0 auto;background-color:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.06)">

      <div style="background-color:${colors.brandColor};padding:24px 32px;text-align:center">
        ${context?.storeLogo ? `<img src="${escapeHtml(context.storeLogo)}" alt="" style="max-height:48px;max-width:200px">` : ''}
        ${state.subject ? `<h1 style="color:#ffffff;font-size:20px;margin:8px 0 0;font-weight:600">${escapeHtml(state.subject)}</h1>` : ''}
      </div>

      <div style="padding:32px;color:${colors.textColor};font-size:15px;line-height:1.7">
        ${greeting ? `<h2 style="font-size:18px;margin:0 0 16px">${escapeHtml(greeting)}</h2>` : ''}

        <div>${body}</div>

        ${showItems ? buildItemsHtml(context?.items, context?.currency) : ''}

        ${context?.total !== undefined && showItems ? `<div style="text-align:center;margin:16px 0;font-size:18px;font-weight:700">الإجمالي: ${context.total.toFixed(2)} ${CURRENCY_LABELS[context?.currency || 'SAR'] || context?.currency || 'SAR'}</div>` : ''}

        ${showCta ? `<div style="text-align:center;margin:24px 0"><a href="${escapeHtml(ctaUrl)}" style="display:inline-block;background-color:${colors.buttonColor};color:#ffffff;text-decoration:none;padding:12px 32px;border-radius:8px;font-size:15px;font-weight:600">${escapeHtml(ctaText)}</a></div>` : ''}
      </div>

      ${showFooter ? `<div style="padding:20px 32px;text-align:center;color:#888888;font-size:12px;border-top:1px solid #f0f0f3">${escapeHtml(storeName)}${context?.storeUrl ? `<br><a href="${escapeHtml(context.storeUrl)}" style="color:${colors.brandColor};text-decoration:none">${escapeHtml(context.storeUrl)}</a>` : ''}</div>` : ''}
    </div>
  </div>
</body>
</html>`;
}
